import { useState, type FormEvent } from 'react';
import { useSearchParams } from 'react-router-dom';
import { CRISIS_HOTLINES } from '@lighthouse/shared';

const CONTACT_CHANNELS = [
  {
    label: 'General enquiries',
    value: 'hi@mylighthouse.ng',
    href: 'mailto:hi@mylighthouse.ng',
  },
  {
    label: 'Privacy & data requests',
    value: 'privacy@mylighthouse.ng',
    href: 'mailto:privacy@mylighthouse.ng',
  },
  {
    label: 'Legal & terms',
    value: 'legal@mylighthouse.ng',
    href: 'mailto:legal@mylighthouse.ng',
  },
] as const;

export function ContactSection() {
  const [searchParams] = useSearchParams();
  const isWaitlist = searchParams.get('app') === 'waitlist';
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState(
    isWaitlist ? "I'd like to join the MyLighthouse mobile app waitlist." : ''
  );

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const subject = encodeURIComponent(`MyLighthouse enquiry from ${name.trim() || 'visitor'}`);
    const body = encodeURIComponent(`${message.trim()}\n\n— ${name.trim()}\n${email.trim()}`);
    window.location.href = `mailto:hi@mylighthouse.ng?subject=${subject}&body=${body}`;
  };

  const [crisisLine] = CRISIS_HOTLINES;

  return (
    <div className="landing-contact-grid">
      {isWaitlist ? (
        <p className="border-primary-200 bg-primary-50 text-primary-900 col-span-full rounded-xl border px-4 py-3 text-sm">
          Join the mobile app waitlist — tell us iOS, Android, or both and we&apos;ll notify you at
          launch.
        </p>
      ) : null}
      <div>
        <p className="landing-section-kicker">Contact</p>
        <h2 className="landing-section-title">Send an enquiry</h2>
        <p className="mt-4 text-sm leading-relaxed text-gray-600 sm:text-base">
          Questions about booking, therapists, assessments, partnerships, or your account? Reach out
          through the channels below. Response expectations will be confirmed as operations mature;
          we aim to reply on business days.
        </p>
        <ul className="mt-6 space-y-3">
          {CONTACT_CHANNELS.map((channel) => (
            <li key={channel.value} className="card-row">
              <span>
                <span className="block text-xs font-semibold uppercase tracking-wide text-gray-400">
                  {channel.label}
                </span>
                <a
                  href={channel.href}
                  className="text-primary-600 text-sm font-semibold hover:underline"
                >
                  {channel.value}
                </a>
              </span>
            </li>
          ))}
          <li className="card-row">
            <span>
              <span className="block text-xs font-semibold uppercase tracking-wide text-gray-400">
                Location
              </span>
              <span className="text-sm font-medium text-gray-800">Lagos, Nigeria</span>
            </span>
          </li>
        </ul>
        <p className="mt-5 text-xs leading-relaxed text-gray-500">
          In a crisis, see our{' '}
          <a href="/crisis.html" className="text-primary-600 font-semibold hover:underline">
            crisis hotlines
          </a>{' '}
          or call{' '}
          {crisisLine ? (
            <a
              href={`tel:${crisisLine.phone}`}
              className="font-semibold text-red-600 hover:underline"
            >
              {crisisLine.phoneDisplay}
            </a>
          ) : (
            '112'
          )}
          .
        </p>
      </div>

      <form
        className="bg-glacier space-y-4 rounded-2xl p-5 shadow-[0_10px_30px_rgba(28,25,23,0.05)] sm:p-6"
        onSubmit={handleSubmit}
      >
        <h3 className="text-midnight text-base font-bold">Send a message</h3>
        <div>
          <label className="label" htmlFor="contact-name">
            Name
          </label>
          <input
            id="contact-name"
            className="input-field"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
            autoComplete="name"
          />
        </div>
        <div>
          <label className="label" htmlFor="contact-email">
            Email
          </label>
          <input
            id="contact-email"
            type="email"
            className="input-field"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            autoComplete="email"
          />
        </div>
        <div>
          <label className="label" htmlFor="contact-message">
            Message
          </label>
          <textarea
            id="contact-message"
            className="input-field min-h-[120px] resize-y"
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            required
            placeholder="How can we help?"
          />
        </div>
        <button type="submit" className="btn-primary w-full sm:w-auto">
          Open in email app
        </button>
        <p className="text-[11px] leading-relaxed text-gray-500">
          This opens your default email client with your message addressed to hi@mylighthouse.ng.
        </p>
      </form>
    </div>
  );
}
